import { useMemo, useState } from 'react';
import { Copy, RefreshCw } from 'lucide-react';
import { Button, Chip, Select } from '@nyce/admin-kit/ui';
import { EmptyState, ErrorBox, Loading, Page, TableScroller, useAsync } from '../../../components/common';
import { copyText } from '../../../identity/format';
import { useTargets } from '../../../identity/session';
import { qualityFor, type FailedQuery } from '../../api/quality';
import { useUrlParams, when } from './params';
import '../../buddy.css';
import './quality.css';

const SINCE = [
  { value: '24h', label: 'Last 24 hours', days: 1 },
  { value: '7d', label: 'Last 7 days', days: 7 },
  { value: '30d', label: 'Last 30 days', days: 30 },
] as const;
type Since = (typeof SINCE)[number]['value'];
const GROUPS = ['error', 'entity', 'none'] as const;
type GroupBy = (typeof GROUPS)[number];
const LIMIT = 500;

/** Group key: the error with numbers masked (same failure, other ids), or the entity. */
export const groupKey = (q: FailedQuery, by: GroupBy) =>
  by === 'error'
    ? q.error.replace(/\d+/g, '#').slice(0, 120)
    : by === 'entity'
      ? q.entity || '(unknown entity)'
      : 'all';

function QueryTable({
  items,
  copied,
  onCopy,
}: {
  items: FailedQuery[];
  copied: string | null;
  onCopy: (q: FailedQuery) => void;
}) {
  return (
    <TableScroller>
      <table className="nyk-ui-table nyk-cards">
        <thead>
          <tr>
            <th>Query</th>
            <th>When</th>
            <th>System</th>
            <th>Entity</th>
            <th>Status</th>
            <th>Error</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {items.map((q) => (
            <tr key={q.id} data-failed={q.id}>
              <td className="nyk-card-title">
                <pre className="bd-query">{q.query}</pre>
                {q.question && <div className="nyk-ui-hint">asked: {q.question}</div>}
                {q.resolvedBy && <div className="nyk-ui-hint">resolved by: {q.resolvedBy}</div>}
              </td>
              <td data-label="When">{when(q.createdAt)}</td>
              <td data-label="System">
                {q.systemName ?? q.systemId}
                {q.userCode && <span className="nyk-ui-hint"> · {q.userCode}</span>}
              </td>
              <td data-label="Entity">{q.entity ?? ''}</td>
              <td data-label="Status">
                <Chip size="sm" className={q.status >= 500 ? 'nyk-tone-red' : 'nyk-tone-amber'}>
                  {q.status}
                </Chip>
              </td>
              <td data-label="Error" className="nyk-text-red">
                <span className="bd-clip" title={q.error}>
                  {q.error}
                </span>
              </td>
              <td className="nyk-row-actions">
                <Button size="sm" variant="ghost" icon={<Copy size={14} />} onClick={() => onCopy(q)}>
                  {copied === q.id ? 'Copied' : 'Copy query'}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableScroller>
  );
}

/** Literal queries the workservers rejected, grouped by error or entity to spot rule gaps; the view lives in the URL. */
export function BuddyFailedQueriesPage() {
  const { active } = useTargets();
  const { get, pick, set } = useUrlParams();
  const system = get('system');
  const since = pick<Since>(
    'since',
    SINCE.map((s) => s.value),
    '7d'
  );
  const groupBy = pick<GroupBy>('groupBy', GROUPS, 'error');
  const api = qualityFor(active);
  const systems = useAsync(() => api.systems(), [active]);
  const list = useAsync(() => {
    const days = SINCE.find((s) => s.value === since)!.days;
    return api.failedQueries({
      system: system || undefined,
      since: new Date(Date.now() - days * 864e5).toISOString(),
      limit: LIMIT,
    });
  }, [active, system, since]);
  const rows = useMemo(() => list.data ?? [], [list.data]);
  const groups = useMemo(() => {
    const m = new Map<string, FailedQuery[]>();
    for (const q of rows) {
      const k = groupKey(q, groupBy);
      m.set(k, [...(m.get(k) ?? []), q]);
    }
    return [...m.entries()].sort((a, b) => b[1].length - a[1].length).map(([key, items]) => ({ key, items }));
  }, [rows, groupBy]);
  const [copied, setCopied] = useState<string | null>(null);
  const copy = async (q: FailedQuery) => {
    if (await copyText(q.query)) {
      setCopied(q.id);
      setTimeout(() => setCopied(null), 1500);
    }
  };
  return (
    <Page
      title="Failed queries"
      description="Literal queries the workservers rejected, grouped by error or entity to spot rule gaps in the knowledge base."
      actions={
        <div className="bd-toolbar">
          <Select
            aria-label="System"
            value={system}
            onChange={(v) => set({ system: v })}
            options={[
              { value: '', label: 'All systems' },
              ...(systems.data ?? []).map((s) => ({ value: s.id, label: s.name || s.id })),
            ]}
          />
          <Select
            aria-label="Since"
            value={since}
            onChange={(v) => set({ since: v === '7d' ? null : v })}
            options={SINCE.map((s) => ({ value: s.value, label: s.label }))}
          />
          <Select
            aria-label="Group by"
            value={groupBy}
            onChange={(v) => set({ groupBy: v === 'error' ? null : v })}
            options={[
              { value: 'error', label: 'Group by error' },
              { value: 'entity', label: 'Group by entity' },
              { value: 'none', label: 'No grouping' },
            ]}
          />
          <Button
            size="sm"
            variant="ghost"
            icon={<RefreshCw size={14} />}
            loading={list.loading}
            onClick={() => void list.reload()}
          >
            Refresh
          </Button>
        </div>
      }
    >
      <ErrorBox error={list.error ?? systems.error} />
      <Loading loading={list.loading && !list.data} />
      {list.data && (
        <p className="nyk-ui-hint" data-testid="failed-count">
          {rows.length} rejected {rows.length === 1 ? 'query' : 'queries'}
          {rows.length >= LIMIT ? ` (the latest ${LIMIT})` : ''}
          {groupBy !== 'none' && ` in ${groups.length} ${groups.length === 1 ? 'group' : 'groups'}`}
        </p>
      )}
      {list.data && !rows.length && (
        <EmptyState
          card
          title="No rejected queries in this period."
          hint="Widen the period or pick another system."
        />
      )}
      {groupBy === 'none'
        ? rows.length > 0 && (
            <div className="nyk-card">
              <QueryTable items={rows} copied={copied} onCopy={(q) => void copy(q)} />
            </div>
          )
        : groups.map((g) => (
            <details key={g.key} className="nyk-card nyk-group" open={groups.length <= 3} data-group={g.key}>
              <summary>
                <span className="bd-group-head">
                  <b>{g.key}</b> <Chip size="sm">{g.items.length}</Chip>
                </span>
              </summary>
              <QueryTable items={g.items} copied={copied} onCopy={(q) => void copy(q)} />
            </details>
          ))}
    </Page>
  );
}
